/**
 * Date utility functions for week calculations and formatting
 */

import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addWeeks,
  subWeeks,
  parseISO,
  isValid,
} from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Get the start date of a week (Monday)
 */
export function getWeekStart(date: Date = new Date()): Date {
  return startOfWeek(date, { weekStartsOn: 1 }); // Monday
}

/**
 * Get the end date of a week (Sunday)
 */
export function getWeekEnd(date: Date = new Date()): Date {
  return endOfWeek(date, { weekStartsOn: 1 }); // Sunday
}

/**
 * Get all days in a week as an array
 */
export function getWeekDays(date: Date = new Date()): Date[] {
  const start = getWeekStart(date);
  const end = getWeekEnd(date);
  return eachDayOfInterval({ start, end });
}

/**
 * Format date as YYYY-MM-DD for API
 */
export function formatDateForAPI(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Format date for display (e.g., "lun., 1 janv.")
 * Uses French locale for translated day/month names
 */
export function formatDateForDisplay(date: Date): string {
  return format(date, 'EEE, d MMM', { locale: fr });
}

/**
 * Format date as full display (e.g., "lundi 1 janvier 2024")
 * Uses French locale for translated day/month names
 */
export function formatDateFull(date: Date): string {
  return format(date, 'EEEE d MMMM yyyy', { locale: fr });
}

/**
 * Format date with day name and number (e.g., "lun. 1")
 * Compact format for calendar cells
 */
export function formatDayNameAndNumber(date: Date): { dayName: string; dayNumber: number } {
  return {
    dayName: format(date, 'EEE', { locale: fr }),
    dayNumber: date.getDate(),
  };
}

/**
 * Format date with full day name and number (e.g., "lundi 1")
 */
export function formatFullDayNameAndNumber(date: Date): { dayName: string; dayNumber: number } {
  return {
    dayName: format(date, 'EEEE', { locale: fr }),
    dayNumber: date.getDate(),
  };
}

/**
 * Get the next week's start date
 */
export function getNextWeek(currentDate: Date = new Date()): Date {
  return addWeeks(getWeekStart(currentDate), 1);
}

/**
 * Get the previous week's start date
 */
export function getPreviousWeek(currentDate: Date = new Date()): Date {
  return subWeeks(getWeekStart(currentDate), 1);
}

/**
 * Parse ISO date string (YYYY-MM-DD) to Date object
 */
export function parseDateFromAPI(dateString: string): Date {
  return parseISO(dateString);
}

/**
 * Check if a date string is valid
 */
export function isValidDate(dateString: string): boolean {
  try {
    const date = parseISO(dateString);
    return isValid(date);
  } catch {
    return false;
  }
}

/**
 * Get current date as YYYY-MM-DD
 */
export function getCurrentDateForAPI(): string {
  return formatDateForAPI(new Date());
}

/**
 * Get week range as { startDate, endDate } for API
 */
export function getWeekRangeForAPI(date: Date = new Date()): {
  startDate: string;
  endDate: string;
} {
  return {
    startDate: formatDateForAPI(getWeekStart(date)),
    endDate: formatDateForAPI(getWeekEnd(date)),
  };
}
